import { useState } from "react";
import type { PostFXSettingsPatch } from "../../../shared/api-contract";
import { useViewer } from "../../../state/ViewerProvider";
import { useEditorStore, useUi } from "../../../state/editor/useEditor";
import { pickFile } from "../../../util/download";
import { savedSceneLostBackdrop } from "../../../util/savedScene";
import { createPreset } from "../../../features/presets/presetsApi";
import { Icon } from "../../kit";
import { CompactCheck, MiniBtn } from "../inspector/rows";
import { LibraryGrid, type LibItem } from "../library/LibraryGrid";
import { useManifest, type ManifestEntry } from "../library/useManifest";
import { reconcileBackdrop, resyncAfterLook, sceneLookState, useSceneLooks } from "../library/sceneLooks";


/* ── Quick looks ────────────────────────────────────────────────────────────
   Ported from the Canvas chrome. These are NOT the .vjson snapshots below:
   a quick look is a small batch of RPCs (background, exposure, one post-FX
   patch), so it composes over whatever model and camera are already loaded
   instead of replaying a whole captured scene. Both kinds are kept — they do
   structurally different things — and the two grids are labelled apart so a
   tile never looks like the other sort.

   Every preset states tonemapBackground/tonemapGround explicitly so switching
   looks is deterministic rather than inheriting whatever the previous look (or
   a vjson import) left set. The two light looks run them OFF, so "E-commerce
   white" is genuinely #ffffff instead of the ACES-rolled ~#e2e2e2; the two dark
   looks keep them ON, which is the filmic backdrop they were tuned against. ── */
const QUICK_LOOKS: {
  id: string;
  label: string;
  grad: string;
  bg: string;
  exposure: number;
  fx: PostFXSettingsPatch;
}[] = [
  {
    id: "classic",
    label: "Classic studio",
    grad: "linear-gradient(135deg,#f3ece0,#d8c9ad)",
    bg: "#efe8db",
    exposure: 1.0,
    fx: { bloom: { enabled: false }, vignette: { enabled: false }, grain: { enabled: false }, grade: { contrast: 1.0, saturation: 1.0 }, tonemapBackground: false, tonemapGround: false },
  },
  {
    id: "ecom",
    label: "E-commerce white",
    grad: "linear-gradient(135deg,#ffffff,#e9edf1)",
    bg: "#ffffff",
    exposure: 1.08,
    fx: { bloom: { enabled: false }, vignette: { enabled: false }, grain: { enabled: false }, grade: { contrast: 0.98, saturation: 0.98 }, tonemapBackground: false, tonemapGround: false },
  },
  {
    id: "noir",
    label: "Editorial noir",
    grad: "linear-gradient(135deg,#26262b,#0c0c0f)",
    bg: "#0f0f12",
    exposure: 0.96,
    fx: { bloom: { enabled: true, intensity: 0.6 }, vignette: { enabled: true, darkness: 0.4 }, grain: { enabled: true }, grade: { contrast: 1.12, saturation: 0.92 }, tonemapBackground: true, tonemapGround: true },
  },
  {
    id: "cine",
    label: "Cinematic warm",
    grad: "linear-gradient(135deg,#3a2f24,#1a1512)",
    bg: "#1a1512",
    exposure: 0.98,
    fx: { bloom: { enabled: true, intensity: 0.4 }, vignette: { enabled: true, darkness: 0.3 }, grain: { enabled: true }, grade: { contrast: 1.08, saturation: 1.08 }, tonemapBackground: true, tonemapGround: true },
  },
];

/* The scene-look library (bundled + curated), the backdrop rule and which
   look is applied live in ../library/sceneLooks.ts, shared with Basic's Scenes
   panel so the two surfaces offer the same scenes and agree on the applied one. */

/** Which quick look is applied — module scope for the same reason as
 *  `sceneLookState.appliedId` (LeftPanel unmounts the body on close). */
let appliedQuickId: string | null = null;

/** A workspace look preset: useManifest spreads `values` into the item, so the
 *  snapshot arrives as a plain field. */
type SavedLook = ManifestEntry & { vjson?: string };

/**
 * Scene looks — one-click curated `.vjson` snapshots (camera, post FX, ground,
 * scene materials) via `loadVjsonUrl`, plus load-your-own from URL or disk
 * (`loadVjsonFile`). Mirrors iJewel's Scene look library.
 */
export function SceneLooksPanel() {
  const { client } = useViewer();
  const r = client.ring;
  const store = useEditorStore();
  const [ui] = useUi();
  const [active, setActive] = useState<string | null>(sceneLookState.appliedId);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [keepFlat, setKeepFlat] = useState(sceneLookState.keepBackdropFlat);
  const [notice, setNotice] = useState<string | null>(null);
  const [activeQuick, setActiveQuick] = useState<string | null>(appliedQuickId);
  /** Bumped after a save so the workspace grid refetches. */
  const [reloadKey, setReloadKey] = useState(0);

  // Curated scene looks from the library, merged with the four the viewer ships.
  // A curated preset carries an absolute fileUrl, so both go through the same
  // loadVjsonUrl — and, critically, the same reconcileBackdrop below.
  const looks = useSceneLooks();
  // Workspace looks, saved by "Save current as Look". These are full of-vjson
  // snapshots carried INSIDE the preset's `values`, not a file on a CDN — so
  // they are applied through loadVjsonFile rather than loadVjsonUrl.
  const { items: saved } = useManifest<SavedLook>(null, "look", reloadKey);

  /** A vjson replays camera, post FX, ground and every scene material — re-read
   * the slices the right rail renders from (shared with Basic's Scenes panel). */
  const resync = () => resyncAfterLook(store);

  /** The shared backdrop rule (library/sceneLooks.ts), said out loud here. */
  const reconcile = async (what: string) => setNotice(await reconcileBackdrop(r, keepFlat, what));

  const apply = async (id: string) => {
    if (busy) return; // two overlapping vjson applies race over the same scene
    setBusy(id);
    setError(null);
    try {
      const look = looks.find((l) => l.id === id);
      if (!look) throw new Error("That scene look is no longer in the library.");
      await r.loadVjsonUrl(look.url);
      sceneLookState.appliedId = id;
      setActive(id);
      // Curated looks run the SAME backdrop reconciliation as the bundled ones:
      // an authored vjson can re-arm tonemapBackground just as easily, which is
      // what makes every snapshot come out filmic grey.
      await reconcile(`“${look.label}”`);
      await resync();
    } catch (e) {
      // A missing /models/<id>.vjson used to reject unhandled while the tile
      // stayed highlighted, i.e. a failed look looked applied.
      setError(e instanceof Error ? e.message : "Could not load that scene look.");
    } finally {
      setBusy(null);
    }
  };

  const loadFile = async () => {
    if (busy) return;
    setBusy("file");
    setError(null);
    // The work has to be wrapped INSIDE the callback: pickFile never rejects
    // (it resolves picked/cancelled), so a malformed .vjson rejecting out of
    // loadVjsonFile would otherwise escape as an unhandled rejection with no
    // message on screen. The `picked` flag owns the busy state for the same
    // reason — pickFile's cancel fallback can resolve while the load is still
    // running, and clearing the spinner there would re-arm the tiles mid-apply.
    let picked = false;
    await pickFile(".vjson,.json", async (f) => {
      picked = true;
      try {
        await r.loadVjsonFile(f);
        sceneLookState.appliedId = null; // a hand-picked file matches no curated tile
        setActive(null);
        await reconcile(`“${f.name}”`);
        await resync();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Could not load that .vjson file.");
      } finally {
        setBusy(null);
      }
    });
    if (!picked) setBusy(null); // dialog dismissed — nothing ran
  };


  /** A quick look is a batch of RPCs over the current scene, not a replay of a
   *  captured one — so it does not touch the camera or the model. */
  const applyQuick = async (id: string) => {
    if (busy) return;
    const look = QUICK_LOOKS.find((l) => l.id === id);
    if (!look) return;
    setBusy(id);
    setError(null);
    try {
      await r.setBackground(look.bg);
      await r.setToneMappingExposure(look.exposure);
      const next = await r.setPostFX(look.fx);
      store.setState("postFX", next);
      await store.refresh("scene");
      appliedQuickId = id;
      setActiveQuick(id);
      // A quick look states the tonemap flags itself, so there is nothing for
      // reconcileBackdrop to argue with — but the two dark looks DO arm them
      // deliberately, and saying so keeps the notice honest.
      setNotice(
        look.fx.tonemapBackground
          ? `“${look.label}” tone-maps the backdrop on purpose — that is the filmic look it was tuned for.`
          : null,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply that look.");
    } finally {
      setBusy(null);
    }
  };

  /** A saved look carries its vjson inline; otherwise identical to a curated one. */
  const applySaved = async (id: string) => {
    if (busy) return;
    const look = saved.find((l) => l.id === id);
    if (!look?.vjson) {
      setError("That saved look has no scene data.");
      return;
    }
    setBusy(id);
    setError(null);
    try {
      await r.loadVjsonFile(new File([look.vjson], "look.vjson", { type: "application/json" }));
      sceneLookState.appliedId = id;
      setActive(id);
      // Saved looks run the SAME backdrop reconciliation as every other kind —
      // an authored vjson can re-arm tonemapBackground just as easily.
      await reconcile(`“${look.label}”`);
      await resync();
      // The one part of a look that can silently not arrive: a backdrop picked
      // from disk that was too large to store inside it. The viewer keeps the
      // current background rather than blanking the scene, so without this the
      // look just quietly looks wrong.
      if (savedSceneLostBackdrop(look.vjson)) {
        setNotice(
          "This look was saved with a local image background that was too large to store. The current background was kept.",
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not apply that look.");
    } finally {
      setBusy(null);
    }
  };

  const saveCurrentAsLook = async () => {
    if (busy) return;
    setBusy("save");
    setError(null);
    try {
      const vjson = await r.exportVjson({ pretty: false });
      await createPreset({ kind: "look", name: `My look ${saved.length + 1}`, values: { vjson } });
      setReloadKey((k) => k + 1);
      setNotice("Saved to this workspace — click it under Your Looks to re-apply.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save the look.");
    } finally {
      setBusy(null);
    }
  };

  const items: LibItem[] = looks.map((l) => ({ id: l.id, label: l.label, ...(l.thumb ? { thumb: l.thumb } : {}) }));

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Quick looks: a batch of RPCs over the current scene. Labelled apart
          from Scene Looks below on purpose — the two grids look identical but
          do structurally different things, and a tile must never imply the
          other sort's behaviour. */}
      <div className="flex items-center gap-1.5">
        <span className="text-[12px] font-semibold text-ed-text-2">Quick looks</span>
      </div>
      <div className={busy ? "pointer-events-none opacity-60" : undefined}>
        <LibraryGrid
          items={QUICK_LOOKS.map((l) => ({ id: l.id, label: l.label, swatch: l.grad }))}
          activeId={activeQuick}
          onSelect={(it) => void applyQuick(it.id)}
          view={ui.libraryView}
        />
      </div>

      {saved.length > 0 ? (
        <>
          <span className="text-[12px] font-semibold text-ed-text-2">Your looks</span>
          <div className={busy ? "pointer-events-none opacity-60" : undefined}>
            <LibraryGrid
              items={saved.map((l) => ({ id: l.id, label: l.label }))}
              activeId={active}
              onSelect={(it) => void applySaved(it.id)}
              view={ui.libraryView}
            />
          </div>
        </>
      ) : null}

      <MiniBtn icon="bookmark_add" busy={busy === "save"} disabled={!!busy} onClick={() => void saveCurrentAsLook()}>
        Save current as Look
      </MiniBtn>

      <div className="flex items-center gap-1.5 border-t border-ed-border pt-3">
        <span className="text-[12px] font-semibold text-ed-text-2">Scene looks</span>
        {busy ? <Icon name="progress_activity" size={13} className="animate-spin text-ed-text-3" /> : null}
      </div>
      {/* LibraryGrid takes no busy/disabled prop, so the pending state is
          enforced here: without it the tiles stayed live during the multi-second
          apply and a second click started an overlapping vjson load. */}
      <div className={busy ? "pointer-events-none opacity-60" : undefined} aria-busy={busy ? true : undefined}>
        <LibraryGrid
          items={items}
          activeId={active}
          onSelect={(it) => void apply(it.id)}
          view={ui.libraryView}
        />
      </div>
      <div className="flex flex-col gap-2">
        <MiniBtn icon="upload" busy={busy === "file"} disabled={!!busy} onClick={() => void loadFile()}>
          {busy === "file" ? "Applying…" : "Load .vjson file"}
        </MiniBtn>
      </div>
      <div
        className="flex items-center justify-between gap-2"
        title="Some looks tone-map the backdrop, which rolls any background colour to a filmic grey — on the canvas and in every snapshot taken from it. Leave this on and a look keeps its own backdrop colour but not that grey."
      >
        <span className="text-[12px] text-ed-text-2">Keep backdrop tone-mapping off</span>
        <CompactCheck
          checked={keepFlat}
          onChange={(v) => {
            sceneLookState.keepBackdropFlat = v;
            setKeepFlat(v);
          }}
        />
      </div>
      {error ? <span className="text-[11px] leading-snug text-ed-red">{error}</span> : null}
      {notice && !error ? <span className="text-[11px] leading-snug text-ed-text-2">{notice}</span> : null}
      <p className="flex items-start gap-1.5 text-[11px] leading-snug text-ed-text-3">
        <Icon name="info" size={13} className="mt-px shrink-0" />
        {busy ? "Applying scene look…" : "A look replays camera, post FX, ground and material settings over the loaded model — it does not swap the model itself."}
      </p>
    </div>
  );
}
